import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAgingReport } from '@/hooks/useOpportunities'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { formatCurrency } from '@/lib/utils'
import { AlertTriangle, Loader2, Info } from 'lucide-react'

type RiskFilter = 'all' | 'high' | 'medium' | 'low'

const TASK_TYPE_LABEL: Record<string, string> = {
  call: 'Ligação', email: 'E-mail', meeting: 'Reunião', task: 'Tarefa', whatsapp: 'WhatsApp',
}

const RISK_STYLE: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  high: { bg: 'bg-red-500/10', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500', label: 'Alto risco' },
  medium: { bg: 'bg-amber-500/10', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500', label: 'Risco médio' },
  low: { bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500', label: 'Em ritmo' },
}

interface AgingReportSectionProps {
  /** Filtro cross-dashboard: nome do responsável vindo do `filters.owner` do Dashboard pai. */
  ownerFilter?: string
  /** Callback ao clicar no nome do owner dentro da tabela — alterna o filtro no Dashboard pai. */
  onOwnerToggle?: (ownerName: string) => void
}

export function AgingReportSection({ ownerFilter, onOwnerToggle }: AgingReportSectionProps = {}) {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<RiskFilter>('all')
  const { data, isLoading, error } = useAgingReport(14)

  const filtered = (data?.items || []).filter(i => {
    if (filter !== 'all' && i.risk !== filter) return false
    if (ownerFilter && (i.owner_name || 'Sem responsável') !== ownerFilter) return false
    return true
  })

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Aging Report — oportunidades por risco
            <div className="relative group">
              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              <div className="absolute left-6 top-0 hidden group-hover:block bg-popover text-popover-foreground border rounded-md p-3 text-xs w-96 shadow-lg z-50 font-normal">
                <p className="mb-2">Lista todas as <strong>oportunidades abertas</strong> classificadas por risco de ficarem paradas.</p>
                <p className="mb-1">A IA combina <strong>4 sinais</strong>:</p>
                <ul className="list-disc ml-4 mb-2 space-y-0.5">
                  <li>Dias sem atividade registrada (notas/emails/calls)</li>
                  <li>Próxima tarefa agendada (ou ausência dela)</li>
                  <li>Tarefas pendentes vencidas</li>
                  <li>Dias até a data prevista de fechamento</li>
                </ul>
                <p className="mb-1"><span className="text-red-600 dark:text-red-400">●</span> <strong>Alto:</strong> &gt;14d sem atividade sem tarefa, ou fechamento atrasado, ou tarefa vencida &gt;7d</p>
                <p className="mb-1"><span className="text-amber-600 dark:text-amber-400">●</span> <strong>Médio:</strong> &gt;7d sem atividade, fechamento em ≤7d, ou tarefa recém-vencida</p>
                <p><span className="text-emerald-600 dark:text-emerald-400">●</span> <strong>Em ritmo:</strong> atividade recente ou tarefa agendada para os próximos dias</p>
              </div>
            </div>
          </CardTitle>
          <div className="flex items-center gap-1 bg-muted rounded-md p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-2.5 py-1 text-xs rounded transition-colors ${
                filter === 'all' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Todas {data && `(${data.total})`}
            </button>
            <button
              onClick={() => setFilter('high')}
              className={`px-2.5 py-1 text-xs rounded transition-colors flex items-center gap-1 ${
                filter === 'high' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              Alto {data && `(${data.high_risk_count})`}
            </button>
            <button
              onClick={() => setFilter('medium')}
              className={`px-2.5 py-1 text-xs rounded transition-colors flex items-center gap-1 ${
                filter === 'medium' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Médio {data && `(${data.medium_risk_count})`}
            </button>
            <button
              onClick={() => setFilter('low')}
              className={`px-2.5 py-1 text-xs rounded transition-colors flex items-center gap-1 ${
                filter === 'low' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Ritmo {data && `(${data.low_risk_count})`}
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Carregando…
          </div>
        )}
        {error && <div className="text-sm text-destructive py-4">Erro ao carregar aging</div>}
        {data && !isLoading && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground border-b">
                  <th className="text-left px-2 py-2 font-normal">Risco</th>
                  <th className="text-left px-2 py-2 font-normal">Oportunidade</th>
                  <th className="text-left px-2 py-2 font-normal">Empresa</th>
                  <th className="text-left px-2 py-2 font-normal">Estágio</th>
                  <th className="text-left px-2 py-2 font-normal">Owner</th>
                  <th className="text-right px-2 py-2 font-normal">Valor</th>
                  <th className="text-right px-2 py-2 font-normal" title="Dias desde a última atividade">Sem ativ.</th>
                  <th className="text-right px-2 py-2 font-normal" title="Dias até a data prevista de fechamento">Fechamento</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted-foreground py-6">
                      Nenhuma oportunidade nessa categoria.
                    </td>
                  </tr>
                )}
                {filtered.map(it => {
                  const r = RISK_STYLE[it.risk]
                  return (
                    <tr
                      key={it.id}
                      className="border-b last:border-0 hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/opportunities/${it.id}`)}
                    >
                      <td className="px-2 py-2 align-top">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${r.bg} ${r.text}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${r.dot}`} />
                          {r.label}
                        </span>
                        <div className="text-[11px] text-muted-foreground mt-1 max-w-[280px] leading-snug">
                          {it.risk_reason}
                        </div>
                        {it.next_task_title && it.next_task_due_date && (
                          <div className="text-[11px] text-blue-600 dark:text-blue-400 mt-0.5 max-w-[280px] leading-snug">
                            📅 {TASK_TYPE_LABEL[it.next_task_type || 'task'] || 'Tarefa'}
                            {it.days_until_next_task != null && (
                              <> {it.days_until_next_task === 0 ? 'hoje' : it.days_until_next_task === 1 ? 'amanhã' : `em ${it.days_until_next_task}d`}</>
                            )}
                            : <span className="text-muted-foreground">{it.next_task_title.length > 35 ? it.next_task_title.slice(0, 35) + '…' : it.next_task_title}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 max-w-[220px] truncate font-medium align-top">{it.name}</td>
                      <td className="px-2 py-2 text-muted-foreground max-w-[180px] truncate align-top">{it.company_name || '—'}</td>
                      <td className="px-2 py-2 text-muted-foreground align-top">{it.stage_label}</td>
                      <td className="px-2 py-2 align-top">
                        {it.owner_name ? (
                          onOwnerToggle ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); onOwnerToggle(it.owner_name as string) }}
                              className={`text-left transition-colors ${ownerFilter === it.owner_name ? 'text-primary font-semibold underline' : 'text-muted-foreground hover:text-foreground hover:underline'}`}
                              title={ownerFilter === it.owner_name ? 'Clique para limpar o filtro' : `Filtrar por ${it.owner_name}`}
                            >
                              {it.owner_name}
                            </button>
                          ) : (
                            <span className="text-muted-foreground">{it.owner_name}</span>
                          )
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right align-top">{formatCurrency(it.value)}</td>
                      <td className="px-2 py-2 text-right align-top">
                        {it.days_since_last_activity == null ? (
                          <span
                            className={it.days_since_created > 14 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-muted-foreground'}
                            title="Sem atividade registrada — contagem desde a criação da oportunidade"
                          >
                            {it.days_since_created}d*
                          </span>
                        ) : (
                          <span className={it.days_since_last_activity > 14 ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>
                            {it.days_since_last_activity}d
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right align-top">
                        {it.days_to_close == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : it.days_to_close < 0 ? (
                          <span className="text-red-600 dark:text-red-400 font-semibold">
                            atrasada ({Math.abs(it.days_to_close)}d)
                          </span>
                        ) : (
                          <span className={it.days_to_close <= 7 ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-muted-foreground'}>
                            em {it.days_to_close}d
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
